import { AfterViewInit, Component, Injector, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/common/base/base.component';
import { WFStatus, WFTableConfig } from '../workflow';
import { Roles } from 'src/app/common/enumerations/enumerations';
import { WFNode } from '../workflow-tree/workflow-tree.component';
import { Observable, Subscription } from 'rxjs';
import { DocumentCategory, DocumentType } from 'src/app/services/documents/documents';
import { init } from 'linkifyjs';
import { end } from '@popperjs/core';

// Declare the global LeaderLine class
declare var LeaderLine: any;

@Component({
  selector: 'workflow-main',
  templateUrl: './workflow-main.component.html',
  styleUrls: ['./workflow-main.component.scss']
})
export class WorkflowMainComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() wfTableConfig!: WFTableConfig;
  @Input() node?: WFNode;
  @Input() editing?: boolean;
  @Input() creating?: boolean;
  @Input() showLines?: boolean;

  wfStatuses?: any[];

  documentType?: DocumentType;
  documentTypes?: DocumentType[];
  documentTypesFiltered?: DocumentType[];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;


  disabled = true;
  private arrowListenerCleanups: (() => void)[] = [];

  constructor(
    protected override injector: Injector,
    private renderer: Renderer2
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.initArrows();

  }

  initArrows() {
    // 🔁 Clean up old listeners
    this.arrowListenerCleanups.forEach(unlisten => unlisten());
    this.arrowListenerCleanups = [];

    // 🧹 Remove existing lines
    if (this.currentLines) {
      this.currentLines.forEach(line => line.remove());
      this.currentLines.clear();
    }

    if (!this.showLines) return;
    const arrows = document.querySelectorAll('.action-arrow');
    this.currentLines = new Map();

    arrows.forEach((el: Element) => {
      const nativeEl = el as HTMLElement;
      const parent = nativeEl.parentElement?.parentElement;
      if (!parent) return;

      // 🧠 Save cleanup functions
      const unlistenEnter = this.renderer.listen(parent, 'mouseenter', () => this.drawLine(nativeEl));
      const unlistenLeave = this.renderer.listen(parent, 'mouseleave', () => this.removeLine(nativeEl));
      this.arrowListenerCleanups.push(unlistenEnter, unlistenLeave);
    });

    const sidenavContent = document.querySelector('.sidenav-content') as HTMLElement;
    if (sidenavContent) {
      // Optional: You could also track and unbind this listener if needed
      sidenavContent.addEventListener('scroll', this.redrawLines.bind(this));
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    this.getWfData();
    setTimeout(() => {
      this.initArrows();
    }, 1000);
  }

  ngOnInit(): void {
    this.wfStatuses = this.utils.getENUM(WFStatus);
    this.loadDocumentTypes();
  }

  loadDocumentTypes() {
    this.documentTypes$ = this.store.select(state => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe(data => {
      if (data?.length) {
        this.documentTypes = data.filter(d => d.category == DocumentCategory.Form).sort((a, b) => this.utils.sort(a.name, b.name));
        this.documentTypesFiltered = this.documentTypes;
      }
    });
  }

  displayNode(node: WFNode) {
    if (this.wfTableConfig == undefined || node.tableId != this.wfTableConfig.id) {
      this.getWfData();
    }
  }

  getWfData() {
    this.disabled = !this.hasRoles([Roles.WFE]) || this.wfTableConfig?.status == 1 || !this.currentUser;
  }

  add() {
    this.wfTableConfig = new WFTableConfig();
    this.wfTableConfig.id = 0;
  }

  private currentLines: Map<HTMLElement, any> = new Map();

  private drawLine(startEl: HTMLElement) {
    const toClass = Array.from(startEl.classList).find(cls => cls.startsWith('to-step-'));
    if (!toClass) return;

    const targetId = toClass.replace('to-step-', '');
    const endEl = document.getElementById(`step-${targetId}`);
    if (!endEl) return;

    let color = '#006BA6'; // fallback default
    let labelClass = 'blue-bg';
    let labelText = 'Approve';

    if (startEl.classList.contains('blue')) {
      color = '#00587C';
      labelClass = 'blue-bg';
      labelText = 'Approve';
    } else if (startEl.classList.contains('red')) {
      color = '#E04E39';
      labelClass = 'red-bg';
      labelText = 'Disapprove';
    } else if (startEl.classList.contains('yellow')) {
      color = '#EAAA00';
      labelClass = 'yellow-bg';
      labelText = 'Unapprove';
    }

    let label = 'Approve';
    if (startEl.classList.contains('red')) label = 'Disapprove';
    else if (startEl.classList.contains('yellow')) label = 'Unapprove';

    let lineOffset = 0;
    if (startEl.classList.contains('blue')) lineOffset = 50;
    else if (startEl.classList.contains('red')) lineOffset = 100;
    else if (startEl.classList.contains('yellow')) lineOffset = 150;

    let startSocketGravity = this.utils.getRandomInt(50, 150);
    let endSocketGravity = this.utils.getRandomInt(50, 150);
    const line = new LeaderLine(
      LeaderLine.pointAnchor(startEl.parentElement?.parentElement!, { x: '95%', y: '20%' }),
      LeaderLine.areaAnchor(endEl, { x: 0, y: 0, width: '100%', height: '100%', radius: 4 }),
      {
        color,
        path: 'fluid',
        startPlug: 'disc',
        endPlug: 'arrow3',
        size: 3,
        startSocketGravity,
        endSocketGravity,
        dropShadow: true,
        animOptions: { duration: 2000, timing: 'ease-in-out' },
        showEffectName: 'draw',

        middleLabel: LeaderLine.captionLabel(label, {
          fontSize: 16,              // number or string
          padding: 4,
          borderRadius: 4,
          className: `${labelClass}`,  // optional extra class
          // outlineColor: '',        // optional
        }),
      }
    );

    line.position();
    this.currentLines.set(startEl, line);
  }

  private removeLine(startEl: HTMLElement) {
    const line = this.currentLines.get(startEl);
    if (line) {
      line.remove();
      this.currentLines.delete(startEl);
    }
  }

  private redrawLines(): void {
    this.currentLines.forEach(line => line.position());
  }


}
